import type { Versione } from './tipi.ts'

/**
 * Novità del gestionale, dalla versione più recente.
 *
 * A ogni rilascio si aggiunge in testa la versione nuova: data, una riga per l'avviso («inBreve») e
 * le cose cambiate per chi usa il gestionale, in parole semplici. Niente migrazioni, niente nomi di
 * file: quelli stanno nel registro di git. La prima versione dell'elenco è quella che l'avviso
 * delle novità annuncia (componenti/AvvisoNovita.tsx). Ricostruite dalla 1.10 alla 1.24 con git log
 * e docs/manuale-utente.md il 31/10/2026.
 */
export const VERSIONI: Versione[] = [
  {
    numero: '1.36.0',
    data: '04/11/2026',
    inBreve: 'calendari delle sedi: orari, chiusure e festività di ogni sede; il «Calendario delle mediazioni» con una '
      + 'riga per mediatore; l’«Agenda» della sede con appuntamenti, mediazioni, turni, stanze e riunioni; «Segnati non '
      + 'disponibile»; il promemoria degli impegni del giorno dopo e, se CIDIS lo accende, il calendario in Outlook e '
      + 'sul telefono.',
    cambiamenti: [
      {
        testo: 'Sedi, linguetta «Orari e chiusure»: l’orario settimanale a fasce (anche estivo, con «Copia l’orario '
          + 'nell’anno dopo»), chiusure, ponti, festa patronale e aperture straordinarie, con le festività nazionali '
          + 'dell’anno. Una mediazione, un ricontatto o un impegno con la sede chiusa danno l’avviso e si fissano lo '
          + 'stesso; una chiusura nuova avvisa chi ha impegni in quei giorni, e l’avviso apre l’agenda del giorno.',
        permesso: ['sede.agenda', 'agenda.leggi'],
        capitolo: 'agenda',
      },
      {
        testo: 'Sedi, linguetta «Stanze»: stanze, sale e postazioni, con posti e caratteristiche. Mediazioni, '
          + 'appuntamenti e prenotazioni scelgono la stanza, e una stanza già occupata blocca.',
        permesso: ['sede.agenda', 'agenda.leggi'],
        capitolo: 'agenda',
      },
      {
        testo: '«Calendario delle mediazioni»: la settimana con una riga per mediatore (o per lingua), i filtri per '
          + 'mediatore, lingua, sede ed ente, in rosso le sovrapposizioni e in arancione la sede chiusa o il mediatore '
          + 'non disponibile. Un clic sullo spazio vuoto programma una mediazione; il file .ics della settimana si '
          + 'consegna al mediatore che non usa il gestionale.',
        permesso: 'agenda.leggi',
        capitolo: 'mediazioni',
      },
      {
        testo: 'Mediazioni: «Stanza», «Ente» e «Si ripete» (ogni settimana, ogni due, ogni mese, al più 26) nella '
          + 'finestra della mediazione; per una serie il mediatore riceve un avviso e un’email soli. «Annulla anche le '
          + 'successive» annulla il resto della serie. Le email al mediatore portano il file .ics da aggiungere al '
          + 'calendario.',
        permesso: 'mediazione.assegna',
        capitolo: 'mediazioni',
      },
      {
        testo: '«Il mio calendario»: le tue mediazioni in giorno, settimana, mese o elenco, con il file .ics da '
          + 'scaricare, e «Segnati non disponibile».',
        permesso: 'mediazione.propria',
        capitolo: 'mediazioni',
      },
      {
        testo: '«Agenda»: gli impegni della sede a strati (appuntamenti, mediazioni, turni, stanze prenotate, '
          + 'riunioni, attività, chiusure), oppure «La mia agenda». Il giorno ha una colonna per stanza o per persona in '
          + 'turno; c’è l’elenco da stampare. Il nome della persona lo vede solo chi segue la scheda. Nel cruscotto, '
          + '«Oggi in sede».',
        permesso: 'agenda.leggi',
        capitolo: 'agenda',
      },
      {
        testo: 'Agenda: riunioni, prenotazioni, attività ed eventi, anche ripetuti, con gli inviti ai colleghi; i turni '
          + 'del personale e dei mediatori dell’albo, con la funzione. Turni e prenotazioni si spostano trascinandoli. '
          + 'Chi è in turno o è invitato riceve un avviso.',
        permesso: ['agenda.scrivi', 'agenda.turni'],
        capitolo: 'agenda',
      },
      {
        testo: '«Segnati non disponibile», nell’agenda e in «Il mio calendario»: un giorno, un periodo o alcune ore, '
          + 'senza motivo. Chi ti mette in turno o ti programma una mediazione riceve un avviso e decide se '
          + '«Programma lo stesso». Nella risposta di assenza della posta c’è «Segna anche nell’agenda».',
        permesso: ['agenda.leggi', 'mediazione.propria'],
        capitolo: 'agenda',
      },
      {
        testo: 'Scheda, «Fissa un appuntamento»: un incontro in sede con durata, sede, stanza e ripetizione. '
          + 'Compare nell’agenda; dopo, «Registra l’intervento». Il promemoria lo chiama «Appuntamento».',
        permesso: 'follow_up.gestisci',
        capitolo: 'agenda',
      },
      {
        testo: 'Promemoria: alle 16 un avviso con gli impegni del giorno dopo (il sabato anche del lunedì): turni, '
          + 'mediazioni, riunioni e prenotazioni, con ora e sede, senza nomi né titoli.',
        permesso: ['agenda.leggi', 'mediazione.propria'],
        capitolo: 'agenda',
      },
      {
        testo: '«Il mio profilo», «Il calendario in Outlook e sul telefono»: un collegamento a «La mia agenda» o a «Le '
          + 'mie mediazioni» che Outlook, Google e il telefono rileggono da soli, senza nomi né titoli. Compare quando '
          + 'CIDIS accende il calendario in abbonamento; il collegamento si vede una volta, vale 12 mesi e si revoca.',
        permesso: ['agenda.leggi', 'mediazione.propria'],
        capitolo: 'telefono',
      },
    ],
  },
  {
    numero: '1.35.3',
    data: '04/11/2026',
    inBreve: 'nella conversazione il messaggio più recente sta in alto; gli allegati si vedono subito sotto mittente e '
      + 'destinatari, con «Scarica»; i messaggi già letti non sono più in grassetto; «Registra intervento» senza «Tema», '
      + 'con la durata in ore e minuti facoltativa e i progetti della sede in ordine alfabetico.',
    cambiamenti: [
      {
        testo: 'Posta: nella conversazione aperta il messaggio più recente sta in alto e i più vecchi in fondo, anche a '
          + 'tutta pagina e nella nuova scheda del browser. Chi preferisce l’ordine di prima sceglie «il messaggio più recente in '
          + 'fondo» in «Elenco e lettura» (la rotella sopra l’elenco).',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: gli allegati stanno subito sotto «Da» e «A», prima dei comandi e degli avvisi, uno per riga con '
          + 'nome, peso e «Scarica». La graffetta con il numero, accanto alla data del messaggio, porta agli allegati e '
          + 'apre il messaggio se è chiuso. Vale anche sul telefono.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: «Scarica PDF» si chiama «Salva il messaggio in PDF»: salva il messaggio, non gli allegati. Se il '
          + 'messaggio ha allegati, la finestra lo dice e ricorda dove scaricarli.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: un messaggio da leggere ha il pallino e mittente e oggetto in grassetto; uno già letto né l’uno '
          + 'né l’altro, nella vista per messaggi e in quella per conversazioni. Prima il mittente restava sempre in '
          + 'grassetto.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: '«Registra intervento»: il campo «Tema» non c’è più. Il tema degli interventi già registrati resta e si '
          + 'legge aprendo l’intervento.',
        permesso: 'intervento.crea',
        capitolo: 'percorsi-interventi',
      },
      {
        testo: '«Registra intervento»: la durata si scrive in ore e minuti (0, 15, 30 o 45) e non è più obbligatoria, '
          + 'né per «Salva» né per «Salva come bozza».',
        permesso: 'intervento.crea',
        capitolo: 'percorsi-interventi',
      },
      {
        testo: '«Registra intervento»: «Finanziamento» si chiama «Progetto». I progetti sono in ordine alfabetico e, '
          + 'all’inizio, solo quelli della sede scelta nel modulo; «Mostra tutti i progetti» mostra anche gli altri e '
          + 'quelli senza sede. Se la sede non ha ancora progetti collegati, compaiono tutti. Cambiando sede l’elenco '
          + 'cambia, e il progetto già scelto resta, con una nota.',
        permesso: 'intervento.crea',
        capitolo: 'percorsi-interventi',
      },
      {
        testo: 'Lingue: c’è l’Ewe. Il persiano si trova sia sotto la F, «Farsi (persiano)», sia sotto la P, «Persiano '
          + '(farsi)»: è la stessa lingua, e scegliendo l’una o l’altra la scheda, la mediazione o l’albo dei mediatori '
          + 'mostrano «Farsi (persiano)».',
      },
      {
        testo: 'Programmi di posta (Thunderbird e simili) sulle caselle condivise: la posta inviata che un programma salva '
          + 'in «Posta in arrivo/Sent», «Sent Items», «Posta inviata» o «Elementi inviati» finisce anche nella cartella '
          + '«Sent» comune, che vedono tutti. I messaggi del 1 e 2 novembre rimasti solo nel gestionale sono stati '
          + 'copiati anche nel programma.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
    ],
  },
  {
    numero: '1.35.2',
    data: '03/11/2026',
    inBreve: 'posta come in Roundcube: la ricerca funziona di nuovo, in tutte le cartelle e per inizi di parola; '
      + 'ogni cartella mostra i suoi messaggi, uno per riga; le bozze sono nella cartella «Bozze»; un messaggio che '
      + 'non parte resta uno solo, con «Non inviato», «Riprova» e «Modifica»; le caselle spente non spediscono più.',
    cambiamenti: [
      {
        testo: 'Posta: il campo «Cerca» rispondeva «Dati non validi» e non trovava niente. Ora cerca.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: accanto al campo «Cerca» scegli dove cercare, come in Roundcube. «Tutte le cartelle», la scelta '
          + 'di partenza, guarda tutta la casella, conversazioni archiviate comprese, tranne Indesiderata e Cestino del '
          + 'server; «Questa cartella» solo quella aperta; «Tutte le caselle» anche le altre caselle che usi. L’ultima '
          + 'scelta resta per la volta dopo.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: ogni risultato della ricerca dice in quale cartella sta, con «archiviata» se la conversazione è '
          + 'archiviata, e con «Tutte le caselle» in quale casella. Vale anche nella vista per singoli messaggi.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: gli inizi di parola da 4 lettere si cercano in tutto l’archivio, anche nei messaggi vecchi e '
          + 'negli indirizzi: «fatt» trova «fattura», «parol» trova paroliamo@cidisonlus.org. Le parole di 2 o 3 '
          + 'lettere valgono intere. Se così non trova niente, cerca anche le parti nel mezzo di una parola nella posta '
          + 'degli ultimi 90 giorni. Per la posta arrivata prima di questa versione gli inizi di parola valgono da quando '
          + 'l’amministratore di sistema ha rifatto l’indice.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: la vista per messaggi è quella di partenza per tutti. Ogni cartella mostra solo i suoi messaggi, '
          + 'una riga per messaggio: «Posta in arrivo» quelli ricevuti, senza le risposte che hai mandato, «Inviata» '
          + 'quelli partiti. Chi aveva scelto e salvato le conversazioni le tiene; restano una scelta di «Elenco e '
          + 'lettura».',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Le bozze sono nella cartella «Bozze» della casella, in cima all’elenco, e un clic le riapre. La bozza si '
          + 'salva dalla prima lettera, subito, e sotto il titolo leggi «Bozza salvata alle 14:05». Chiudendo la pagina '
          + 'o passando a un’altra app le ultime modifiche partono subito; se qualcosa non è ancora salvato il browser '
          + 'chiede conferma.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Se il server di posta non accetta un messaggio, la finestra per scrivere resta aperta con «Non inviato» '
          + 'e il motivo. Il messaggio resta uno solo, nelle «Bozze», con «Non inviato» in rosso: «Riprova» lo '
          + 'rispedisce com’è, «Modifica» lo riapre. Prima ogni tentativo lasciava un messaggio in più, come se fosse '
          + 'partito: quelli di prima ora sono nelle «Bozze» con «Non inviato».',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Una casella spenta, come una vecchia casella personale, si consulta soltanto, con la scritta «Casella '
          + 'spenta: non spedisce». Non compare nel campo «Da» e da lì non parte niente; le bozze scritte da lì si '
          + 'riaprono e passano a un’altra casella.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
    ],
  },
  {
    numero: '1.35.1',
    data: '03/11/2026',
    inBreve: 'gli allegati infetti vanno in quarantena, separati dagli altri file: nel messaggio resta il nome, e non si '
      + 'scaricano da nessuna parte; la Direzione e la Referente privacy li gestiscono dalla linguetta «Quarantena». '
      + 'Più persone sulla stessa casella da Thunderbird, ognuna con la sua password. Testi di guida, email e avvisi '
      + 'rivisti.',
    cambiamenti: [
      {
        testo: 'Posta: un allegato che l’antivirus dichiara infetto va subito in quarantena, in un archivio solo per questi '
          + 'file. Nel messaggio resta il nome con «Infetto, in quarantena: non si scarica», senza comando: non si scarica '
          + 'nemmeno con il .eml, l’inoltro o dall’archivio delle caselle.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Archivio delle caselle: gli allegati infetti non si scaricano più neanche da qui.',
        permesso: ['utente.gestisci', 'posta.archivio'],
        capitolo: 'posta',
      },
      {
        testo: 'Posta, linguetta «Quarantena»: l’elenco degli allegati infetti con casella, mittente, data, nome, tipo e '
          + 'peso. «Scarica in formato sicuro» li riporta da Glacier come zip cifrato con password, mostrata una volta, '
          + 'per chi li deve analizzare o per un’autorità; «Elimina definitivamente» chiede il nome del file. Ogni '
          + 'recupero, scaricamento ed eliminazione resta nel registro; dei recuperi e degli scaricamenti la Referente '
          + 'privacy riceve un avviso.',
        permesso: 'posta.quarantena_gestisci',
        capitolo: 'posta',
      },
      {
        testo: 'Testi rivisti: guida, manuale, email automatiche, avvisi e messaggi dell’interfaccia, con accenti, '
          + 'concordanze e frasi più chiare. Nelle email e negli avvisi le date sono in formato italiano, all’ora di Roma, '
          + 'e al posto dei codici tecnici ci sono i nomi di ruoli e sedi. Nessun comando ha cambiato nome.',
        permesso: null,
      },
      {
        testo: 'Posta da Thunderbird o da un altro programma: una casella di gruppo, per esempio amministrazione@, la '
          + 'possono usare più persone, ognuna con la sua password. Il nome utente resta l’indirizzo della casella; '
          + 'cartelle e letto/non letto sono gli stessi per tutte. Ciò che una persona spedisce dal programma arriva '
          + 'nella «Inviata» del gestionale con «inviato da» e il suo nome.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: '«Caselle e ricezione» e «Caselle condivise» → «Modifica»: il riquadro «Programmi di posta» dice chi usa la '
          + 'casella da un programma, da quando e l’ultimo invio; «Revoca» chiude solo quella persona. La credenziale '
          + 'si revoca da sola se la persona esce dalla casella, passa a «legge» o la sua utenza si disattiva.',
        permesso: 'utente.gestisci',
        capitolo: 'posta',
      },
    ],
  },
  {
    numero: '1.35.0',
    data: '03/11/2026',
    inBreve: 'collaboratori esterni con accesso @esterni.cidisonlus.org e caselle di servizio; cestino della posta; '
      + 'indirizzi da copiare e «inviato da» sulle caselle di gruppo; stampa, PDF ed .eml dei messaggi; «Solo non '
      + 'lette»; bandierine ed etichette sulle conversazioni; risposta di assenza e regole; conferma di lettura e '
      + 'gruppi della rubrica; più conversazioni insieme, cartelle tue, ordine e vista per messaggi come in Roundcube; '
      + '«(esterno)» accanto al nome nel messenger.',
    cambiamenti: [
      {
        testo: '«Aggiungi una persona»: la spunta «È esterno?» è per i collaboratori esterni che non devono avere la '
          + 'casella cidisonlus.org. La persona entra con l’iniziale del nome e il cognome @esterni.cidisonlus.org (per '
          + 'esempio mrossi@esterni.cidisonlus.org) e ha lì la sua casella, che scrive e riceve solo dentro CIDIS. Il '
          + 'rapporto è «Esterno», la scadenza dell’utenza è facoltativa; l’invito parte alla sua email personale.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
      {
        testo: 'Nella «Modifica» di un collaboratore esterno: «Riceve da fuori» e «Invia fuori» per la sua casella, e '
          + '«Crea anche la casella cidisonlus.org». Si entra sempre con l’indirizzo @esterni. Le sue caselle condivise '
          + 'sono le «Caselle di servizio», anche con altre persone. Ogni deroga resta nel registro.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
      {
        testo: 'Ai collaboratori esterni si può dare qualunque ruolo, come al personale; senza scadenza dell’utenza i '
          + 'loro incarichi non hanno il limite dei dodici mesi degli altri esterni.',
        permesso: 'ruolo.assegna',
        capitolo: 'utenti',
      },
      {
        testo: '«Importa da foglio»: la colonna «è esterno». Con «sì» la persona entra con l’indirizzo '
          + '@esterni.cidisonlus.org, con la stessa proposta e gli stessi controlli di «Aggiungi una persona». Scarica di '
          + 'nuovo il modello: ha la colonna nuova.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
      {
        testo: 'Pagina di accesso: nel menu accanto al nome utente c’è anche @esterni.cidisonlus.org, per i '
          + 'collaboratori esterni.',
        permesso: null,
        capitolo: 'accesso',
      },
      {
        testo: 'Posta: dalla casella @esterni si scrive solo alle caselle di CIDIS; prima dell’invio il gestionale '
          + 'segnala un destinatario fuori da CIDIS, con l’indirizzo da togliere. Le email automatiche del gestionale '
          + 'arrivano nella casella @esterni.',
        permesso: null,
        capitolo: 'posta',
      },
      {
        testo: 'Messenger: chi ha il rapporto esterno con CIDIS compare in Element come «Nome Cognome (esterno)». '
          + 'Se il rapporto torna interno, l’etichetta sparisce entro mezz’ora.',
        permesso: 'chat.usa',
        capitolo: 'messenger',
      },
      {
        testo: 'Posta: nella conversazione aperta, accanto ad «Archivia», c’è «Sposta nel cestino». A sinistra, sotto '
          + 'le cartelle, la voce «Cestino» dice quante conversazioni contiene e le mostra; aperta una, «Ripristina» la '
          + 'riporta fra le aperte. Con la colonna nascosta il cestino è nel menu delle cartelle.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: la tendina sopra l’elenco sceglie fra conversazioni «aperte» e «archiviate»; le «cestinate» sono '
          + 'nel «Cestino». La ricerca non guarda nel cestino, salvo quando sei nel cestino. La cartella cestino '
          + 'arrivata dal server di posta si chiama ora «Cestino del server».',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: le conversazioni rimaste 30 giorni nel cestino passano alla regola di cancellazione: entrano in '
          + '«In cancellazione» con l’etichetta «dal cestino» e il preavviso, e si possono mettere in conservazione. '
          + 'Con la regola spenta il cestino non si svuota. Sulle caselle di gruppo il cestino è uno per tutti.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: nel messaggio aperto gli indirizzi di «Da», «A» e «Cc» si selezionano, e «Copia» accanto a '
          + 'ciascuno lo copia. Anche nell’archivio delle caselle.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: sulle caselle di gruppo e di progetto i messaggi partiti dicono chi li ha mandati, «inviato da '
          + 'Nome Cognome», nella conversazione e nella cartella «Inviata», anche se partiti da un programma di posta. '
          + 'Chi riceve l’email non lo vede.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: ogni messaggio aperto ha «Stampa» (un foglio pulito, senza menu), «Scarica PDF» (dalla finestra di '
          + 'stampa, con «Salva come PDF») e «Scarica .eml» (il messaggio come file di posta, ricomposto dal gestionale, '
          + 'senza gli allegati non controllati dall’antivirus). Anche nell’archivio delle caselle.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: «Solo non lette», sopra l’elenco, mostra solo le conversazioni da leggere, nella cartella, in '
          + '«Tutte le caselle» e con la ricerca.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: se una casella di CIDIS si legge anche da un programma di posta (credenziale personale), '
          + 'quello che spedisci dal gestionale con quella casella (risposte, inoltri e bozze compresi) compare anche '
          + 'nella posta inviata del programma, già letto. Le email ai colleghi da quella casella passano dal server '
          + 'di posta e arrivano dopo qualche secondo. Letto e non letto, spostamenti e cancellazioni restano '
          + 'separati fra il programma e il gestionale.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: in deroga, chi gestisce le utenze può far inoltrare la posta di una casella a un indirizzo fuori '
          + 'CIDIS («Caselle e ricezione», «Inoltro in deroga», con il motivo). Si inoltra com’è, con il mittente vero, '
          + 'solo la posta che arriva da fuori CIDIS; per quella interna (colleghi, caselle @esterni, email automatiche) '
          + 'all’indirizzo esterno arriva solo un avviso con il numero delle email e il collegamento, al più uno ogni '
          + '10 minuti. La copia nel gestionale resta, la posta indesiderata non si inoltra. La Referente privacy '
          + 'riceve un avviso, vede gli inoltri attivi nella pagina «Posta» e li revoca in un clic.',
        permesso: ['utente.gestisci', 'posta.inoltri_controlla'],
        capitolo: 'posta',
      },
      {
        testo: 'Posta: bandierine ed etichette sulle conversazioni. «Bandierina ed etichette», nella conversazione aperta '
          + 'e su ogni riga dell’elenco, mette una bandierina in sei colori (la riga resta di quel colore) ed etichette '
          + 'di testo, per tutti quelli che usano la casella o solo per te; da lì le etichette si creano, si rinominano '
          + 'e si eliminano. Sopra l’elenco due filtri, per bandierina e per etichetta, cercano in tutte le cartelle '
          + 'della casella. Bandierine ed etichette restano nel gestionale: i programmi di posta non le vedono.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: «Assenza e regole», sotto le cartelle della casella. La «Risposta di assenza» risponde da sola '
          + 'mentre sei via, dal giorno al giorno che scegli, una volta ogni 7 giorni per mittente e mai a liste, '
          + 'messaggi automatici e posta indesiderata; le ultime risposte si vedono lì. Le «Regole» smistano i '
          + 'messaggi che arrivano secondo mittente, destinatario e oggetto: li spostano in una cartella, li segnano '
          + 'come letti o fermano le regole successive; «Applica ora alla Posta in arrivo» le passa sui messaggi già '
          + 'arrivati.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta, come in Roundcube: nel messaggio che scrivi «Chiedi la conferma di lettura» e «Priorità alta». Se '
          + 'chi ti scrive chiede la conferma, aprendo il messaggio scegli «Invia la conferma» o «Ignora»: non parte da '
          + 'sola. Quando arriva la conferma, nel messaggio spedito leggi «Letto da … il …»; la priorità alta è un '
          + '«!» rosso.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: «Inoltra» porta anche gli allegati del messaggio (senza quelli non ancora controllati '
          + 'dall’antivirus), e ogni messaggio della conversazione ha il suo «Inoltra», per quando gli allegati stanno '
          + 'in un messaggio prima delle risposte. «Inoltra come allegato» manda il messaggio come file .eml. «Mostra '
          + 'originale», sotto gli indirizzi, mostra intestazioni e sorgente, anche nell’archivio delle caselle. Le '
          + 'intestazioni originali si conservano da questa versione; per i messaggi di prima sono ricostruite.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Rubrica della posta: i gruppi di contatti, che nel messaggio diventano i loro indirizzi; «Importa da file» '
          + 'da vCard e CSV di Gmail e Outlook, con l’anteprima e i doppioni segnalati; «Esporta in vCard» ed «Esporta in '
          + 'CSV», che restano nel registro eventi.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: nella conversazione aperta «È indesiderata» sposta i messaggi ricevuti nella cartella '
          + '«Indesiderata», e «Non è indesiderata» li riporta nella «Posta in arrivo». Il filtro antispam del server '
          + 'di CIDIS impara da quello che segni.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: i messaggi segnati nel vecchio Roundcube sono diventati una bandierina rossa per tutti sulla loro '
          + 'conversazione, anche archiviata o nel cestino. Dove c’era già una bandierina per tutti è rimasta la sua.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: più conversazioni insieme. Ogni riga dell’elenco ha la sua casella da spuntare (Maiusc+clic per '
          + 'un intervallo) e sopra c’è «Seleziona tutte»; sulle righe spuntate la barra archivia, sposta nel cestino '
          + 'o ripristina, segna come lette o da leggere, sposta in una cartella, mette bandierine ed etichette.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: cartelle tue nelle caselle del gestionale. «Cartelle…», sotto le cartelle, le crea (anche una '
          + 'dentro l’altra), le rinomina e le elimina: le conversazioni di una cartella eliminata tornano nella Posta '
          + 'in arrivo. Le conversazioni si spostano con «Sposta in…» o trascinando le righe sulle cartelle. «Segna '
          + 'come letta» nella conversazione aperta, «Segna tutte come lette» sopra l’elenco della cartella.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: «Elenco e lettura», l’ingranaggio sopra l’elenco: ordine per data, mittente, oggetto o '
          + 'dimensione; vista per conversazioni o per singoli messaggi; righe per pagina; un messaggio aperto letto '
          + 'subito, dopo qualche secondo o mai da solo; risposta sopra o sotto il messaggio citato. Valgono per te '
          + 'su ogni dispositivo. Le scorciatoie da tastiera di Roundcube: Canc, R, A, F, frecce, Invio, Esc, Ctrl+A; '
          + '«?» le elenca.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: si allega qualsiasi file tranne i programmi, come in Gmail: fatture .xml e .p7m, .zip, '
          + 'presentazioni, .msg, audio, video, anche i file senza tipo. Prima alcuni tipi restavano fuori e il '
          + 'messaggio partiva senza. Più file insieme, anche trascinandoli sulla finestra; un file rifiutato o non '
          + 'caricato lo dice la riga degli allegati, e «Invia» aspetta i caricamenti o chiede se spedire senza.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
    ],
  },
  {
    numero: '1.34.0',
    data: '03/11/2026',
    inBreve: '«Ricordami su questo computer» con Windows Hello o Touch ID e 4 ore di inattività; avvisi per i '
      + 'messaggi diretti del messenger; per i mediatori, il comune in cui lavorano e il recapito.',
    cambiamenti: [
      {
        testo: '«Ricordami su questo computer»: dopo l’accesso, sul tuo computer, premi «Ricordami» e conferma con Windows '
          + 'Hello (impronta, volto o PIN) o Touch ID. Per 30 giorni dall’ultimo uso la pagina di accesso offre «Entra '
          + 'con Windows Hello» o «Entra con Touch ID», senza password né codice del secondo fattore; la password resta '
          + 'sempre possibile. Solo sul tuo computer, non su quelli condivisi.',
        permesso: null,
        capitolo: 'accesso',
      },
      {
        testo: 'Sul computer ricordato la sessione resta aperta 4 ore senza attività invece di una. In «Il mio profilo», '
          + '«Computer ricordati» li elenca con l’ultimo uso; «Revoca» toglie quello che non usi più.',
        permesso: null,
        capitolo: 'accesso',
      },
      {
        testo: 'Azzerando il secondo fattore o disattivando un’utenza si revocano anche i telefoni e i computer '
          + 'ricordati di quella persona: non riaprono più la sessione senza password.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
      {
        testo: 'Messenger: un messaggio in una chat a due fa arrivare nel gestionale «Nuovo messaggio nel messenger», '
          + 'in basso a destra, come notifica del computer e sul telefono con l’app CIDIS. Al massimo uno ogni quarto '
          + 'd’ora per chat; i gruppi e i canali non avvisano. Se silenzi una chat in Element, non avvisa nemmeno qui.',
        permesso: 'chat.usa',
        capitolo: 'messenger',
      },
      {
        testo: 'Mediatori: ogni mediatore ha il «Comune in cui lavora», obbligatorio quando lo aggiungi o lo modifichi, '
          + 'e un «Recapito» facoltativo, dove riceve le persone. I mediatori che non hanno ancora il comune si '
          + 'possono scegliere lo stesso: nella riga leggi «comune mancante», e il filtro «Comune» → '
          + '«— non indicato —» li trova.',
        permesso: 'mediatore.gestisci',
        capitolo: 'albo-mediatori',
      },
      {
        testo: 'Mediatori: nell’elenco c’è la colonna «Comune», con il comune in cui lavora ciascuno, e il filtro per '
          + 'trovare chi non l’ha. Anche il foglio di «Scarica l’elenco» ha la colonna.',
        permesso: 'mediatore.leggi',
        capitolo: 'albo-mediatori',
      },
      {
        testo: 'Mediazioni: nella tendina dei mediatori c’è il comune in cui lavorano, per esempio «Rossi Anna — '
          + 'Perugia». Dopo «Programma», se il mediatore ha un recapito, il messaggio e la riga della mediazione '
          + 'mostrano «Recapito del mediatore», da dire alla persona.',
        permesso: 'mediazione.assegna',
        capitolo: 'mediazioni',
      },
      {
        testo: 'Importa da Excel: il tracciato ha la colonna «Comune in cui lavora». Una riga senza entra lo stesso, '
          + 'con un avviso; il mediatore va poi completato da «Modifica».',
        permesso: 'mediatore.gestisci',
        capitolo: 'albo-mediatori',
      },
    ],
  },
  {
    numero: '1.33.2',
    data: '03/11/2026',
    inBreve: 'quando programmi una mediazione e scegli la lingua, compaiono anche i mediatori senza lingue '
      + 'nell’albo.',
    cambiamenti: [
      {
        testo: 'Mediazioni: scelta la lingua, la tendina dei mediatori mostra prima chi la parla e poi, in un gruppo '
          + 'a parte, chi non ha lingue indicate nell’albo. Prima questi sparivano, e per molte lingue compariva '
          + '«Nessun mediatore trovato».',
        permesso: 'mediazione.assegna',
        capitolo: 'mediazioni',
      },
      {
        testo: 'Mediatori: nel filtro «Lingua» c’è «— non indicata —», per trovare e completare i mediatori senza '
          + 'lingue nell’albo.',
        permesso: 'mediatore.leggi',
        capitolo: 'mediazioni',
      },
    ],
  },
  {
    numero: '1.33.1',
    data: '02/11/2026',
    inBreve: 'le email scritte dal gestionale alle caselle lette da un programma di posta arrivano anche nel programma.',
    cambiamenti: [
      {
        testo: 'Posta: le email scritte dal gestionale a una casella di CIDIS letta anche da un programma di posta '
          + '(credenziale personale) arrivano anche nel programma, non solo nella webmail. Prima un inoltro o '
          + 'un messaggio di un collega a quella casella restava nel gestionale.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
    ],
  },
  {
    numero: '1.33.0',
    data: '02/11/2026',
    inBreve: 'avvisi in basso a destra per le email e le notifiche nuove, e a richiesta notifiche del computer.',
    cambiamenti: [
      {
        testo: 'Avvisi sullo schermo: con il gestionale aperto, un riquadro in basso a destra per ogni email nuova '
          + 'nelle tue caselle (mittente e oggetto) e per ogni notifica nuova (il titolo). Un clic apre la '
          + 'conversazione o la notifica; si chiude da solo dopo otto secondi. Si spengono in «Notifiche», «Avvisi su '
          + 'questo computer».',
        permesso: null,
        capitolo: 'cruscotto',
      },
      {
        testo: 'Notifiche del computer: con «Attiva le notifiche del computer» e il permesso del browser, gli avvisi '
          + 'arrivano anche con il gestionale in secondo piano. Dicono solo «Nuova email in» e la casella, oppure «Nuovo '
          + 'avviso nel gestionale», perché restano nel centro notifiche del computer.',
        permesso: null,
        capitolo: 'cruscotto',
      },
      {
        testo: 'Notifiche: «Posta nuova in …» apre la casella dell’avviso, anche quando è una casella di gruppo o di '
          + 'progetto. Prima si apriva la casella personale, o l’ultima scelta, e l’email non si trovava.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
    ],
  },
  {
    numero: '1.32.0',
    data: '02/11/2026',
    inBreve: 'posta: il selettore delle caselle con i messaggi da leggere, la rubrica con i suggerimenti, la '
      + 'ricerca in tutto l’archivio, l’indirizzo completo del mittente, accanto a ogni messaggio «CIDIS» o «esterno», '
      + 'grassetto, elenchi e collegamenti nei messaggi, «Scrivi email» dalla scheda della persona e la posta a '
      + 'tutta pagina.',
    cambiamenti: [
      {
        testo: 'Posta a tutta pagina: sul computer caselle, elenco e conversazione arrivano in fondo allo schermo e '
          + 'scorrono ciascuno per conto suo; il testo dei messaggi si legge intero, non più in un riquadro basso. La '
          + 'colonna delle caselle si nasconde, la conversazione si apre a tutta pagina o in una nuova scheda, e la '
          + 'finestra per scrivere è larga quanto la pagina.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: in cima alla pagina, anche sul telefono, il selettore delle caselle mostra ogni indirizzo con le '
          + 'sue etichette e i messaggi da leggere; si sceglie quale casella aprire. All’ingresso si apre la casella '
          + 'personale, o l’ultima scelta; «Tutte le caselle» resta una voce del selettore. I messaggi da leggere '
          + 'compaiono anche accanto alle caselle nella colonna di sinistra.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: ogni casella ha la sua rubrica, che si riempie da sola a ogni invio ed è partita dalla posta già '
          + 'inviata. Mentre scrivi in A, Cc e Ccn compaiono i suggerimenti, colleghi di CIDIS compresi. Nella '
          + 'linguetta «Rubrica» cerchi, correggi il nome, togli un contatto o gli scrivi.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: nel messaggio aperto mittente e destinatari si leggono per intero, con l’indirizzo, per esempio '
          + '«Anna Rossi <anna.rossi@ente.it>»; a messaggio chiuso l’indirizzo compare passando sopra il nome.',
        permesso: ['posta.usa', 'utente.gestisci'],
        capitolo: 'posta',
      },
      {
        testo: 'Posta: nella conversazione ogni messaggio, anche chiuso, ha accanto al mittente l’etichetta «CIDIS», '
          + '«esterno», «esterno, non verificato» o «falso CIDIS?». Per la posta dello storico guarda il dominio del '
          + 'mittente e lo dice quando ci passi sopra. Le stesse etichette sono nell’archivio delle caselle.',
        permesso: ['posta.usa', 'utente.gestisci', 'posta.archivio'],
        capitolo: 'posta',
      },
      {
        testo: 'Posta: il campo «Cerca» trova le parole intere in tutto l’archivio, anche nel testo dei messaggi, nei nomi '
          + 'degli allegati e nella posta arrivata dal vecchio server; prima guardava solo le conversazioni più recenti. '
          + 'Con più parole escono i messaggi che le contengono tutte; maiuscole e accenti non contano. Le parti di '
          + 'parola si cercano negli ultimi 90 giorni.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: mentre cerca, la pagina dice «Ricerca in corso…», e «Cerca nei messaggi più vecchi» fa '
          + 'continuare la ricerca quando si ferma a un certo punto dell’archivio. «Nessun messaggio trovato» compare '
          + 'solo quando la ricerca è arrivata in fondo.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: sopra il messaggio c’è la barra della formattazione, con grassetto, corsivo, sottolineato, '
          + 'elenchi, citazione, collegamenti e «togli formattazione»; da tastiera Ctrl+B, Ctrl+I, Ctrl+U e Ctrl+K. Il '
          + 'collegamento mostra prima dove porta. Il testo incollato da Word o da Outlook perde caratteri, colori e '
          + 'immagini e tiene paragrafi, elenchi e grassetti.',
        permesso: ['posta.usa', 'utente.gestisci'],
        capitolo: 'posta',
      },
      {
        testo: 'Scheda: «Scrivi email» apre il messaggio della Posta con l’email della persona e la tua casella. '
          + 'Dopo l’invio la conversazione si collega alla scheda, e le risposte arrivano lì.',
        permesso: 'posta.aggancia',
        capitolo: 'scheda',
      },
    ],
  },
  {
    numero: '1.31.1',
    data: '02/11/2026',
    inBreve: 'la sessione resta aperta per un’ora senza attività, invece di mezz’ora.',
    cambiamenti: [
      {
        testo: 'Accesso: senza attività la sessione si chiude dopo un’ora, non più dopo mezz’ora.',
        permesso: null,
        capitolo: 'accesso',
      },
    ],
  },
  {
    numero: '1.31.0',
    data: '02/11/2026',
    inBreve: 'posta: i collegamenti si toccano nel testo e mostrano prima dove portano; gli allegati non ancora '
      + 'controllati si scaricano dopo un avviso.',
    cambiamenti: [
      {
        testo: 'Posta: per le caselle in deroga, una credenziale personale permette di leggere con IMAP e spedire da un '
          + 'programma di posta (Outlook, Thunderbird, Mail). Ogni messaggio spedito dal programma finisce anche nella '
          + 'posta inviata della casella nel gestionale, e il registro eventi lo attribuisce alla persona della '
          + 'credenziale. Le credenziali le crea e le revoca l’amministrazione di sistema.',
        permesso: 'utente.gestisci',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: i collegamenti si toccano direttamente nel testo del messaggio. Si apre la finestra «Aprire il '
          + 'collegamento?» con il sito vero a cui porta, l’indirizzo completo e gli avvisi; il riquadro «Collegamenti '
          + 'nel messaggio» sotto il testo non c’è più.',
        permesso: ['posta.usa', 'utente.gestisci', 'posta.archivio'],
        capitolo: 'posta',
      },
      {
        testo: 'Posta: gli allegati non ancora controllati dall’antivirus si scaricano dopo un avviso, che invita ad '
          + 'aprirli solo se ci si aspettava quel file; lo scaricamento resta nel registro. Quelli bloccati '
          + 'dall’antivirus non si scaricano.',
        permesso: ['posta.usa', 'utente.gestisci'],
        capitolo: 'posta',
      },
    ],
  },
  {
    numero: '1.30.1',
    data: '02/11/2026',
    inBreve: 'posta: gli allegati in cima a ogni messaggio, la graffetta con il numero sui messaggi della conversazione '
      + 'e, in alto, tutti gli allegati della conversazione.',
    cambiamenti: [
      {
        testo: 'Posta: gli allegati stanno in cima al messaggio, sotto mittente e data, con il loro numero; prima erano in '
          + 'fondo, dopo il testo. Nella conversazione la graffetta con il numero accanto al mittente dice quale '
          + 'messaggio ha allegati anche quando è chiuso.',
        permesso: ['posta.usa', 'utente.gestisci', 'posta.archivio'],
        capitolo: 'posta',
      },
      {
        testo: 'Posta: nelle conversazioni con più messaggi, in alto, «Allegati nella conversazione» raccoglie tutti i '
          + 'file, messaggio per messaggio; se tocchi mittente e data, si apre quel messaggio.',
        permesso: ['posta.usa', 'utente.gestisci'],
        capitolo: 'posta',
      },
      {
        testo: 'Posta sul telefono: la conversazione aperta resta dentro lo schermo, senza tagliarsi a destra.',
        permesso: ['posta.usa', 'utente.gestisci'],
        capitolo: 'posta',
      },
    ],
  },
  {
    numero: '1.30.0',
    data: '02/11/2026',
    inBreve: 'sul cruscotto il riquadro per mettere il gestionale sul telefono; il riquadro «App per il telefono» '
      + 'del profilo e il menu laterale più ordinati.',
    cambiamenti: [
      {
        testo: 'Cruscotto: un riquadro ti invita a mettere il gestionale sul telefono. Dal computer mostra un codice QR '
          + 'da inquadrare con la fotocamera; da Android ha il pulsante «Scarica l’app»; dall’iPhone i tre tocchi per '
          + 'la schermata Home. Non compare se hai già un telefono connesso; la crocetta lo nasconde per trenta giorni.',
        permesso: null,
        capitolo: 'telefono',
      },
      {
        testo: '«Il mio profilo», «App per il telefono»: dal computer, Android e iPhone stanno affiancati, con il '
          + 'codice QR che apre la pagina sul telefono; dal telefono compare prima il tuo, l’altro resta chiuso '
          + 'sotto.',
        permesso: null,
        capitolo: 'telefono',
      },
      {
        testo: 'Il menu laterale ha di nuovo lo spazio sopra il nome di ogni gruppo (Rete, Comunicazione, Controllo…).',
        permesso: null,
      },
    ],
  },
  {
    numero: '1.29.3',
    data: '02/11/2026',
    inBreve: 'posta: ogni email dice se viene davvero da CIDIS o da fuori, e i collegamenti si aprono dopo che hai '
      + 'visto dove portano; messenger sul telefono: dove confermare il nuovo dispositivo.',
    cambiamenti: [
      {
        testo: 'Posta: sopra ogni messaggio ricevuto una fascia dice da dove viene davvero, secondo i controlli del server '
          + 'di ricezione e non secondo il mittente scritto: verde «Da CIDIS, verificato», grigia «Comunicazione '
          + 'automatica del gestionale», gialla «Esterno a CIDIS» (con «mittente non verificato» se il suo dominio non lo '
          + 'conferma), rossa «Si presenta come CIDIS ma non viene dai server di CIDIS». Nell’elenco le conversazioni '
          + 'esterne hanno l’etichetta «esterno», quelle sospette «falso CIDIS?».',
        permesso: ['posta.usa', 'utente.gestisci'],
        capitolo: 'posta',
      },
      {
        testo: 'Posta: sotto ogni messaggio il riquadro «Collegamenti nel messaggio» elenca i collegamenti con il '
          + 'dominio in grassetto; nel testo non si aprono. Se ne tocchi uno, la finestra «Aprire il collegamento?» '
          + 'mostra il dominio in grande e l’indirizzo completo: controlla che sia il sito che ti aspetti prima di '
          + 'premere «Apri il collegamento». Un avviso rosso segnala il collegamento che fa vedere un indirizzo e porta '
          + 'altrove; altri avvisi gli indirizzi numerici, i siti senza https, i nomi con lettere di altri alfabeti, '
          + 'gli indirizzi accorciati e i reindirizzamenti. Vale anche per l’archivio delle caselle.',
        permesso: ['posta.usa', 'utente.gestisci', 'posta.archivio'],
        capitolo: 'posta',
      },
      {
        testo: 'Messenger sul telefono, ultimo passo: Element chiede di confermare il nuovo dispositivo. Si conferma dal '
          + 'messenger dove sei già entrato la prima volta, di solito il gestionale sul computer (pagina «Messenger»), '
          + 'controllando che le emoji coincidano; senza un altro dispositivo, con la chiave di recupero.',
        permesso: 'chat.usa',
        capitolo: 'messenger',
      },
    ],
  },
  {
    numero: '1.29.1',
    data: '02/11/2026',
    inBreve: 'l’app CIDIS per Android si scarica dal gestionale e resta connessa; sull’iPhone il gestionale va nella '
      + 'schermata Home e si apre con Face ID; il messenger sul telefono con Element Classic.',
    cambiamenti: [
      {
        testo: 'Il telefono resta connesso: dopo il primo accesso l’app Android si apre senza password per 30 giorni '
          + 'dall’ultimo uso; sull’iPhone, con il gestionale nella schermata Home, «Attiva Face ID» fa lo stesso e '
          + 'ogni apertura chiede Face ID. In «Il mio profilo», «Telefoni connessi» elenca i telefoni e li scollega.',
        permesso: null,
        capitolo: 'telefono',
      },
      {
        testo: 'Caselle di gruppo: «Disattiva e archivia» disattiva la casella, fa rifiutare i messaggi nuovi e la porta '
          + 'nell’archivio delle caselle in un passo solo.',
        permesso: 'utente.gestisci',
        capitolo: 'posta',
      },
      {
        testo: '«Il mio profilo»: nuovo riquadro «App per il telefono». Su Android scarichi l’app CIDIS da lì, senza '
          + 'Google Play, con lo sblocco del telefono e gli avvisi sullo schermo. Sull’iPhone trovi i passi per aggiungere '
          + 'il gestionale alla schermata Home con Safari.',
        permesso: null,
        capitolo: 'telefono',
      },
      {
        testo: 'Dal browser del telefono un avviso in cima alla pagina ti invita a installare l’app (sull’iPhone, il '
          + 'gestionale nella schermata Home): «Installa» porta al riquadro «App per il telefono», «Più tardi» lo '
          + 'nasconde per sette giorni. Dal computer l’avviso ha un codice QR da inquadrare con il telefono.',
        permesso: null,
        capitolo: 'telefono',
      },
      {
        testo: 'Messenger sul telefono: si usa l’app Element Classic, non Element X, che con l’accesso del gestionale '
          + 'non entra. I passi per iPhone e Android sono in cima alla pagina «Messenger» aperta dal telefono e nel '
          + 'riquadro «App per il telefono», sezione «Messenger».',
        permesso: 'chat.usa',
        capitolo: 'messenger',
      },
    ],
  },
  {
    numero: '1.29.0',
    data: '01/11/2026',
    inBreve: 'firma di CIDIS in ogni email, allegati grandi come collegamento, email a scadenza, posta fra '
      + 'colleghi cifrata, archivio delle caselle, accesso con il solo nome utente e recupero della password '
      + 'approvato.',
    cambiamenti: [
      {
        testo: 'Posta: ogni email che scrivi parte con la firma di CIDIS: nome, denominazione, sede legale, la tua sede '
          + 'principale, telefoni, email, la nota sull’ambiente e gli avvisi di riservatezza. È già nel testo quando '
          + 'apri il messaggio e la puoi modificare; se cambi «Da», diventa quella della casella scelta.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Nuova pagina «Il mio profilo», nel menu sotto «Il tuo accesso»: scrivi cellulare e telefono d’ufficio, '
          + 'che entrano nella firma, e vedi l’anteprima. I numeri li cambi solo tu.',
        permesso: null,
        capitolo: 'posta',
      },
      {
        testo: 'Caselle di gruppo e di progetto: «Senza il mio nome», nel messaggio, toglie il tuo nome dalla firma. '
          + 'Chi gestisce la casella può scriverle una firma propria, con {nome} dove va il nome di chi scrive.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Impostazioni: nuovo riquadro «Firma delle email» con denominazione, sede legale, nota sull’ambiente e '
          + 'avvisi di riservatezza, comuni a tutte le firme.',
        permesso: 'impostazione.modifica',
      },
      {
        testo: 'Sedi: l’indirizzo della sede compare nella firma delle email di chi ha lì la sede principale.',
        permesso: 'sede.gestisci',
      },
      {
        testo: 'Posta: gli allegati troppo grandi per i server di posta partono come collegamento, in una tabella '
          + '«Allegati» prima della firma; i piccoli restano allegati. Si allegano file fino a 200 MB, e il '
          + 'compositore dice prima dell’invio quali file partiranno come collegamento.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Nel messaggio spedito, «Scaricamenti» accanto al file dice quando l’hanno scaricato; «Revoca il '
          + 'collegamento» lo spegne subito.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: «Collegamenti agli allegati» elenca tutti i collegamenti con i loro scaricamenti.',
        permesso: 'posta.collegamenti_registro',
        capitolo: 'posta',
      },
      {
        testo: 'Email a scadenza: nel messaggio, «Scadenza». «Scade il messaggio»: chi sta fuori da CIDIS riceve un '
          + 'avviso con il collegamento a una pagina che lo mostra fino alla data scelta, anche con una password da '
          + 'dire a voce. «Scadono solo gli allegati»: gli allegati partono come collegamento e scadono.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Nel messaggio spedito a scadenza: «Scadenza e registro» dice chi ha aperto la pagina; «Cambia la '
          + 'scadenza» e «Revoca ora».',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta fra colleghi: fra le caselle di CIDIS il messaggio non passa più dal server di posta, arriva '
          + 'subito nella casella del collega ed è cifrato con la chiave della sua casella. Un indirizzo di CIDIS '
          + 'che non c’è più ferma l’invio.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: «Archivia» su una casella personale, di gruppo o di progetto la disattiva e la porta '
          + 'nell’«Archivio delle caselle». Chi le scrive riceve un messaggio chiaro, in italiano e in inglese, con '
          + 'l’indirizzo di contatto di CIDIS.',
        permesso: 'utente.gestisci',
        capitolo: 'posta',
      },
      {
        testo: 'Archivio delle caselle: le caselle archiviate si consultano in sola lettura (ogni apertura resta nel '
          + 'registro). Dopo 10 giorni passano in Glacier: prima si può estendere il termine, bloccare il passaggio o '
          + 'estrarre i messaggi da tenere.',
        permesso: 'posta.archivio',
        capitolo: 'posta',
      },
      {
        testo: 'Dalle caselle già in Glacier si estraggono singoli messaggi o si risveglia tutta la casella. Ogni recupero '
          + 'ha il suo stato, il tempo trascorso e quello stimato; quando è pronto arriva un avviso.',
        permesso: 'posta.archivio',
        capitolo: 'posta',
      },
      {
        testo: 'Pagina di accesso: il campo dice «Nome utente o email». Con la casella CIDIS basta il nome utente, per '
          + 'esempio «dramondetti»: accanto c’è già «@cidisonlus.org». Chi entra con un’altra email la scrive per intero.',
        capitolo: 'accesso',
      },
      {
        testo: '«Il mio profilo», nel gruppo «Il tuo accesso»: aggiungi la tua email personale di recupero. Ti arriva un '
          + 'codice di sei cifre da scrivere per confermarla. Se lavori in CIDIS e non l’hai, una riga in cima alla pagina '
          + 'te la chiede.',
        capitolo: 'accesso',
      },
      {
        testo: '«Ho dimenticato la password»: scrivi il nome utente o l’email e la richiesta va a chi gestisce gli utenti. '
          + 'Quando la approva, il collegamento per la password nuova arriva alla tua email di recupero e vale un’ora. Il '
          + 'secondo fattore resta.',
        capitolo: 'accesso',
      },
      {
        testo: '«Aggiungi una persona»: «Email personale» e «Crea la casella CIDIS», spuntata per i rapporti interni. '
          + 'L’indirizzo proposto è l’iniziale del nome e il cognome, e puoi cambiarlo; se è preso, leggi le alternative '
          + 'libere. L’invito parte all’email personale.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
      {
        testo: '«Importa da foglio»: nel modello ci sono «email personale», «crea casella» e «indirizzo CIDIS».',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
      {
        testo: 'Utenti: la colonna e il filtro «Email di recupero» dicono chi non l’ha ancora. Le richieste di recupero '
          + 'della password arrivano in «Approvazioni»; chi non ha l’email di recupero riceve un «Nuovo invito».',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
    ],
  },
  {
    numero: '1.28.0',
    data: '01/11/2026',
    inBreve: 'più persone scrivono insieme la stessa email, i progetti hanno una casella di posta propria e la '
      + 'posta scritta dalle persone parte dal server di CIDIS.',
    cambiamenti: [
      {
        testo: 'Posta: la stessa email la scrivete insieme, in contemporanea, come in un documento condiviso. Sopra i '
          + 'campi leggi chi sta scrivendo e dove. La bozza si salva da sola dalla prima modifica e la trovi a '
          + 'sinistra, sotto «Bozze in scrittura».',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Nelle caselle condivise le bozze sono di chi scrive dalla casella: chi apre una bozza già aperta da un '
          + 'collega scrive insieme a lui.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: '«Invita a scrivere»: inviti un collega su una bozza precisa. Vede e modifica solo quella bozza, e il '
          + 'messaggio a cui rispondi; non vede il resto della casella e non spedisce. L’invito si revoca con «×» e '
          + 'vale subito.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Spedisce solo chi scrive dalla casella. All’invio la bozza si ferma: gli altri leggono «Inviata da '
          + 'Nome Cognome». Le bozze a cui ti invitano stanno sotto «Bozze condivise con te», e ricevi un avviso.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: le email scritte dalle persone partono dal server di posta di CIDIS, firmate; quelle '
          + 'automatiche del gestionale (inviti, codici, avvisi) da notifiche.sistema@cidisonlus.org, e a queste non '
          + 'si risponde. Ogni messaggio arriva al massimo a 100 destinatari e 25 MB; una casella che spedisce troppo '
          + 'in poco tempo si ferma per un po’ e la pagina lo dice.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Progetti: nel progetto nuovo spunta «Il progetto ha una casella di posta dedicata», scegli «progetto-» '
          + 'o «pg-» e scrivi il resto dell’indirizzo. Nei progetti che ci sono già, riquadro «Posta del progetto», '
          + '«Crea la casella».',
        permesso: 'progetto.gestisci',
        capitolo: 'progetti',
      },
      {
        testo: 'La casella del progetto la aprono il referente CIDIS e chi ha l’incarico di Project Manager su quel '
          + 'progetto; gli altri solo se aggiunti, con «legge» oppure «legge e scrive». Nessun inoltro verso le '
          + 'caselle personali: si apre da «Posta» o con «Apri in Posta» dalla pagina del progetto.',
        permesso: ['progetto.leggi', 'posta.usa'],
        capitolo: 'progetti',
      },
      {
        testo: 'Posta: accanto alle caselle dei progetti leggi «progetto»; con il solo livello «legge» leggi «sola '
          + 'lettura», e la conversazione si apre senza «Rispondi» né «Archivia».',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: anche le caselle di gruppo hanno il livello «legge» oppure «legge e scrive». Chi le aveva già '
          + 'resta con «legge e scrive».',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Chi gestisce gli utenti legge in cima alla pagina quante caselle sono da associare. «Associale» apre '
          + '«Caselle condivise»: «Associa a un progetto», «Associa a persone» o «È la casella personale di…».',
        permesso: 'utente.gestisci',
        capitolo: 'posta',
      },
      {
        testo: 'Posta, «Caselle condivise» → «Caselle di progetto»: le caselle dei progetti che esistono già sul '
          + 'vecchio server si collegano con il loro indirizzo. Gli inoltri del vecchio server non si ricreano.',
        permesso: 'utente.gestisci',
        capitolo: 'posta',
      },
      {
        testo: 'Finché la posta di CIDIS non passa al gestionale, una casella ospitata dice «La ricezione nel '
          + 'gestionale non è ancora attiva».',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
    ],
  },
  {
    numero: '1.27.1',
    data: '01/11/2026',
    inBreve: 'ogni notte il gestionale fa una copia cifrata dei suoi dati, oltre a quella del fornitore.',
    cambiamenti: [
      {
        testo: 'Ogni notte, alle 4, il gestionale fa una copia cifrata dell’intero database e la conserva per 30 '
          + 'giorni. Se una notte non riesce, Direzione e amministrazione ricevono un avviso.',
        permesso: 'utente.gestisci',
      },
    ],
  },
  {
    numero: '1.27.0',
    data: '01/11/2026',
    inBreve: 'la posta si aggiorna da sola mentre la pagina è aperta, la Referente privacy decide al posto della '
      + 'DPO, e la webmail si accende dall’interfaccia con le approvazioni.',
    cambiamenti: [
      {
        testo: 'Posta: mentre la pagina è aperta le caselle che guardi si rileggono da sole ogni uno o due minuti e '
          + 'l’elenco ogni trenta secondi. Sotto la casella leggi «aggiornata alle …».',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'La Referente privacy prende le decisioni che spettavano alla DPO: approva l’accensione delle funzioni '
          + 'di controllo con la Direzione, concede le supervisioni della posta, decide l’accesso straordinario e la '
          + 'visione delle registrazioni. Chi chiede non decide la propria richiesta.',
        permesso: 'approvazione.decidi',
        capitolo: 'approvazioni',
      },
      {
        testo: 'Posta: sezioni «In cancellazione» e «In conservazione». Finché CIDIS non accende la regola di '
          + 'cancellazione, non si cancella niente; con la regola accesa, ogni conversazione resta in preavviso prima '
          + 'di sparire e con «Metti in conservazione» la tieni.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: la Referente privacy vede le supervisioni in corso e le revoca. L’avviso in calce cambia solo '
          + 'con la sua approvazione e va solo sulle email scritte dalle persone, non su inviti e codici.',
        permesso: 'posta.supervisione_concedi',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: nuova linguetta «Caselle e ricezione», pronta per la posta ospitata nel gestionale (nessuna '
          + 'password di casella). Si userà quando la posta di cidisonlus.org passerà al nuovo server.',
        permesso: 'utente.gestisci',
        capitolo: 'posta',
      },
      {
        testo: 'Amministrazione → Impostazioni: ogni funzione di controllo ha «Chiedi l’approvazione». Approvata, si '
          + 'spuntano le condizioni e «Attiva» la accende; l’approvazione vale per una sola accensione.',
        permesso: 'impostazione.leggi',
        capitolo: 'approvazioni',
      },
      {
        testo: 'Posta: «Chiedi una supervisione» per leggere una casella altrui, con motivo e durata; si apre solo '
          + 'dopo l’approvazione. Chi ha la casella riceve un avviso a ogni conversazione aperta.',
        permesso: 'posta.supervisiona',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: a webmail accesa, ai messaggi scritti si aggiunge in fondo l’avviso sull’archiviazione, con il '
          + 'collegamento all’informativa di CIDIS. Le caselle si collegano al server di posta solo in modo cifrato.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Posta: la pagina non scorre più di lato quando si apre una conversazione lunga.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Guida: immagini nuove per Palestra, Compila dal documento, Secondo fattore e Posta.',
        capitolo: 'palestra',
      },
    ],
  },
  {
    numero: '1.26.1',
    data: '31/10/2026',
    inBreve: 'il secondo fattore non è più obbligatorio: si entra anche senza, e una riga in cima alla pagina '
      + 'ti propone di attivarlo.',
    cambiamenti: [
      {
        testo: 'Il secondo fattore è facoltativo per tutti i ruoli. Chi non l’ha attivato entra come prima e vede in '
          + 'cima alla pagina «Proteggi il tuo accesso», che porta a «Secondo fattore»; la riga si chiude con la '
          + 'crocetta. Chi l’ha già attivato continua a usarlo.',
        capitolo: 'accesso',
      },
    ],
  },
  {
    numero: '1.26.0',
    data: '31/10/2026',
    inBreve: 'il secondo fattore da attivare con l’app del telefono, la lettura del documento nella nuova scheda e '
      + 'le due firme per i progetti con un altro titolare.',
    cambiamenti: [
      {
        testo: '«Secondo fattore», nel gruppo «Il tuo accesso» del menu: lo attivi con l’app del telefono e ricevi '
          + 'dieci codici di recupero. È obbligatorio da subito per Direzione, Amministratore, DPO, Referente privacy, '
          + 'Auditor e Supporto, dal 17/11/2026 per tutti gli altri.',
        capitolo: 'accesso',
      },
      {
        testo: 'Senza telefono, all’accesso «Usa un codice di recupero». Con il telefono nuovo, «Sostituisci l’app».',
        capitolo: 'accesso',
      },
      {
        testo: 'Amministrazione → Utenti: «Azzera il secondo fattore» per chi ha perso telefono e codici, e chi deve '
          + 'ancora attivarlo si vede nell’elenco.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
      {
        testo: 'Nuova scheda: «Compila dal documento» legge i dati dalla foto o dal PDF del documento d’identità e '
          + 'li propone nel modulo. Si controllano sempre prima di salvare.',
        permesso: 'persona.crea',
        capitolo: 'nuova-scheda',
      },
      {
        testo: 'Scheda: nel riquadro «Moduli dei progetti» si vedono il modulo del progetto e il modulo CIDIS, '
          + 'firmati o da firmare, e «Stampa i due moduli» li stampa insieme. Il modulo del progetto mancante '
          + 'compare fra le cose da completare.',
        permesso: 'persona.leggi',
        capitolo: 'scheda',
      },
      {
        testo: 'Importazione delle schede anonimizzate: la conoscenza dell’italiano non è più obbligatoria; senza, la '
          + 'scheda entra fra quelle da completare.',
        permesso: 'persona.crea',
        capitolo: 'anonimizzate',
      },
    ],
  },
  {
    numero: '1.25.0',
    data: '31/10/2026',
    inBreve: 'la Guida illustrata, la Palestra per provare senza toccare i dati veri, l’importazione delle schede '
      + 'anonimizzate e il modulo del destinatario dei progetti.',
    cambiamenti: [
      {
        testo: 'Nel menu «Aiuto» ci sono la «Guida», con i passi e le immagini di ogni funzione, e queste «Novità».',
        capitolo: 'cruscotto',
      },
      {
        testo: '«Palestra», accanto al tuo nome: con i tuoi ruoli lavori sulle schede inventate della sede di '
          + 'formazione, finché la spegni.',
        capitolo: 'palestra',
      },
      {
        testo: 'Beneficiari: «Importa schede anonimizzate» inserisce da un foglio Excel le schede con le sole iniziali.',
        permesso: 'persona.crea',
        capitolo: 'anonimizzate',
      },
      {
        testo: 'Progetti: il «Modulo del destinatario», a partire dal modello FAMI 2021-2027, si stampa compilato '
          + 'dalla scheda della persona e si carica firmato.',
        permesso: ['progetto.leggi', 'persona.leggi'],
        capitolo: 'progetti',
      },
      {
        testo: 'Pagina «Formazione»: rimette la sede di formazione come all’inizio e dà password nuove agli utenti di prova.',
        permesso: 'formazione.gestisci',
        capitolo: 'formazione',
      },
      {
        testo: 'La sede di formazione non entra più negli elenchi, nei report e nelle esportazioni dei dati veri.',
        permesso: ['report.aggregato', 'export.aggregato'],
      },
      { testo: 'Più colleghi dello stesso ufficio possono entrare insieme la mattina senza essere bloccati.' },
    ],
  },
  {
    numero: '1.24.0',
    data: '30/10/2026',
    inBreve: 'albo dei mediatori con i filtri per lingua e paese, l’importazione da Excel e «Dai l’accesso» '
      + 'anche per coordinatori e Project Manager.',
    cambiamenti: [
      {
        testo: 'Albo dei mediatori: nuovi filtri «Lingua» e «Paese di origine». Il paese lo vede chi gestisce l’albo.',
        permesso: ['mediatore.leggi', 'mediatore.gestisci'],
        capitolo: 'albo-mediatori',
      },
      {
        testo: 'Coordinatori e Project Manager gestiscono l’albo dei mediatori come la Direzione e scaricano '
          + 'l’elenco con «Scarica l’elenco».',
        permesso: ['mediatore.leggi', 'mediatore.gestisci'],
        capitolo: 'albo-mediatori',
      },
      {
        testo: 'Coordinatori e Project Manager usano «Dai l’accesso» e «Nuovo invito» per far entrare i mediatori nuovi.',
        permesso: 'mediatore.gestisci',
        capitolo: 'albo-mediatori',
      },
      {
        testo: '«Importa da Excel»: si inseriscono molti mediatori nuovi da un foglio con le tendine. Chi c’è già si salta.',
        permesso: 'mediatore.gestisci',
        capitolo: 'albo-mediatori',
      },
      {
        testo: 'I Project Manager creano e modificano i progetti.',
        permesso: 'progetto.leggi',
        capitolo: 'progetti',
      },
      { testo: 'Nuove voci nell’elenco delle lingue, chieste da CIDIS.' },
      {
        testo: 'Messenger: gli spazi di tutte le sedi sono in «Esplora stanze» e si entra con un clic.',
        permesso: 'chat.usa',
        capitolo: 'messenger',
      },
    ],
  },
  {
    numero: '1.23.0',
    data: '30/10/2026',
    inBreve: 'caselle di posta di gruppo, gestionale più comodo sul telefono, messenger in una finestra separata.',
    cambiamenti: [
      {
        testo: 'Posta: le caselle di gruppo (per esempio qualita@ e privacy@) stanno accanto alla tua. «Tutte le '
          + 'caselle» le mostra in una lista sola; sotto la conversazione leggi chi l’ha aperta.',
        permesso: 'posta.usa',
        capitolo: 'posta',
      },
      {
        testo: 'Chi gestisce gli utenti crea le caselle di gruppo e le assegna alle persone.',
        permesso: 'utente.gestisci',
        capitolo: 'posta',
      },
      {
        testo: 'Sul telefono: menu a scomparsa, comandi più grandi e campi più leggibili.',
        capitolo: 'telefono',
      },
      {
        testo: 'Pagina «Notifiche»: «Telefoni collegati», per gli avvisi sull’app CIDIS.',
        capitolo: 'telefono',
      },
      {
        testo: 'Messenger: «Apri in una finestra separata», da tenere accanto al gestionale.',
        permesso: 'chat.usa',
        capitolo: 'messenger',
      },
    ],
  },
  {
    numero: '1.22.0',
    data: '29/10/2026',
    inBreve: 'schede con «Dati anonimizzati» e profilo «CIDIS Dipendenti e Collaboratori».',
    cambiamenti: [
      {
        testo: 'Schede con «Dati anonimizzati» per chi non può firmare l’informativa: solo iniziali, niente dati '
          + 'che identificano la persona, niente consensi. Gli interventi si registrano lo stesso.',
        permesso: ['persona.crea', 'persona.modifica'],
        capitolo: 'anonimizzate',
      },
      {
        testo: 'Nuovo profilo «CIDIS Dipendenti e Collaboratori», per registrare la formazione del personale.',
        permesso: ['persona.crea', 'persona.modifica'],
        capitolo: 'nuova-scheda',
      },
      {
        testo: 'Nuovo ruolo «Referente privacy»: controlla il registro eventi e decide le approvazioni ordinarie.',
      },
    ],
  },
  {
    numero: '1.21.0',
    data: '29/10/2026',
    inBreve: 'chat riservata della scheda, anche con la persona assistita.',
    cambiamenti: [
      {
        testo: 'Chat riservata nelle schede con note riservate: «Équipe» per il personale e «Con la persona». Si '
          + 'chiude con «Chiudi la chat» e si cancella dopo 90 giorni.',
        permesso: ['riservato.leggi', 'riservato.scrivi'],
        capitolo: 'area-riservata',
      },
    ],
  },
  {
    numero: '1.20.0',
    data: '29/10/2026',
    inBreve: 'il messenger si apre dentro il gestionale.',
    cambiamenti: [
      {
        testo: 'Il messenger si apre dentro il gestionale, senza un’altra password.',
        permesso: 'chat.usa',
        capitolo: 'messenger',
      },
    ],
  },
  {
    numero: '1.19.0',
    data: '28/10/2026',
    inBreve: 'nuovo messenger cifrato, con gli spazi di CIDIS e delle sedi.',
    cambiamenti: [
      {
        testo: 'Nuovo messenger cifrato: spazio CIDIS e spazio della sede, con «Generale» e «Avvisi». Dalla scheda, '
          + '«Riferimento per il messenger» copia numero e collegamento senza il nome.',
        permesso: 'chat.usa',
        capitolo: 'messenger',
      },
      { testo: 'Utenti: «Scarica l’elenco» in un foglio Excel.', permesso: 'utente.leggi', capitolo: 'utenti' },
      { testo: 'Enti: «Finanziatore?» e «Partner formale?».', permesso: 'ente.leggi', capitolo: 'enti' },
      {
        testo: 'Albo dei mediatori: email e numero dell’albo CIDIS nell’anagrafica.',
        permesso: 'mediatore.gestisci',
        capitolo: 'albo-mediatori',
      },
    ],
  },
  {
    numero: '1.18.0',
    data: '28/10/2026',
    inBreve: 'mediatori su più sedi e incarichi su più sedi in un colpo solo.',
    cambiamenti: [
      {
        testo: 'Albo dei mediatori: le sedi in cui lavora ogni mediatore, oppure «Lavora in tutte le sedi». '
          + 'Quando programmi una mediazione, in cima ci sono i mediatori della sede.',
        permesso: ['mediatore.leggi', 'mediatore.gestisci', 'mediazione.assegna'],
        capitolo: 'mediazioni',
      },
      {
        testo: 'Incarichi: più sedi insieme, oppure «Tutte le sedi», con un solo atto.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
    ],
  },
  {
    numero: '1.17.0',
    data: '27/10/2026',
    inBreve: '«Dai l’accesso» ai mediatori direttamente dall’albo.',
    cambiamenti: [
      {
        testo: '«Dai l’accesso» nella riga del mediatore: utenza, incarico di Mediatore e collegamento all’albo in '
          + 'un passo solo.',
        permesso: 'mediatore.gestisci',
        capitolo: 'albo-mediatori',
      },
    ],
  },
  {
    numero: '1.16.0',
    data: '25/10/2026',
    inBreve: 'enti con dipartimenti e referenti già alla creazione, e «Unisci i doppioni».',
    cambiamenti: [
      {
        testo: 'Enti: «Dipartimenti, settori e filiali» e referenti si inseriscono già con l’ente. Il «Ruolo» del '
          + 'referente si chiama «Funzione».',
        permesso: 'ente.leggi',
        capitolo: 'enti',
      },
      {
        testo: '«Aggiungi un referente» anche mentre registri un servizio, senza lasciare il modulo.',
        permesso: 'servizio_erogato.registra',
        capitolo: 'registro-servizi',
      },
      {
        testo: '«Unisci i doppioni» per gli enti registrati due volte.',
        permesso: 'ente.gestisci',
        capitolo: 'enti',
      },
      {
        testo: 'Dal 26/10 anche operatori interni e coordinatori inseriscono e modificano gli enti.',
        permesso: 'ente.gestisci',
        capitolo: 'enti',
      },
    ],
  },
  {
    numero: '1.15.0',
    data: '23/10/2026',
    inBreve: '«Modifica la scheda», attività collettive con partner e partecipanti, unione di due schede.',
    cambiamenti: [
      {
        testo: 'Scheda: «Modifica la scheda» in testa, «Inserita da» nell’intestazione, «Paese d’origine» al posto di '
          + '«Stato di nascita». I dati obbligatori mancanti sono evidenziati.',
        permesso: 'persona.cerca',
        capitolo: 'scheda',
      },
      {
        testo: 'Attività collettive: progetto, partner, comune in cui si svolgono e partecipanti (uomini, donne, '
          + 'minori) invece dei posti.',
        permesso: 'attivita_collettiva.leggi',
        capitolo: 'attivita-collettive',
      },
      { testo: 'La Direzione unisce due schede della stessa persona.', permesso: 'persona.unisci', capitolo: 'beneficiari' },
      {
        testo: 'Il modulo firmato è un foglio solo: si carica una volta per tutte le dichiarazioni.',
        permesso: 'consenso.registra',
        capitolo: 'consensi',
      },
      {
        testo: 'Documenti: «In verifica antivirus», con l’ora in cui saranno disponibili.',
        permesso: 'documento.carica',
        capitolo: 'scheda',
      },
      {
        testo: 'Il Mediatore si assegna per area e non ha la posta; l’Operatore esterno solo su un progetto, in '
          + 'sola lettura.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
    ],
  },
  {
    numero: '1.14.0',
    data: '22/10/2026',
    inBreve: 'albo dei mediatori con «Disattiva» nella riga, anagrafica e contratto.',
    cambiamenti: [
      {
        testo: 'Albo dei mediatori: «Disattiva» e «Riattiva» nella riga; anagrafica e contratto, con l’avviso '
          + 'quando il contratto finisce.',
        permesso: 'mediatore.gestisci',
        capitolo: 'albo-mediatori',
      },
      {
        testo: 'Utenti: «Elimina dall’elenco» per gli autori del vecchio gestionale che non devono più comparire.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
    ],
  },
  {
    numero: '1.13.0',
    data: '21/10/2026',
    inBreve: '«Dai l’accesso» a chi usava il vecchio gestionale.',
    cambiamenti: [
      {
        testo: '«Dai l’accesso» a chi usava il vecchio gestionale: stessa utenza, con il lavoro che aveva registrato.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
    ],
  },
  {
    numero: '1.12.1',
    data: '21/10/2026',
    inBreve: 'i comuni capoluogo si trovano subito.',
    cambiamenti: [
      { testo: 'Cercando «Napoli», «Perugia» o «Roma» fra i comuni, il capoluogo compare per primo.' },
    ],
  },
  {
    numero: '1.12.0',
    data: '21/10/2026',
    inBreve: 'solo le 11 sedi di CIDIS e incarichi su tutta CIDIS anche per gli operatori.',
    cambiamenti: [
      { testo: 'Restano le 11 sedi di CIDIS: schede e interventi degli sportelli sono passati alla loro sede.' },
      {
        testo: 'Operatori interni, Project Manager e coordinatori possono avere un incarico su tutta CIDIS.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
      {
        testo: 'L’elenco degli utenti mostra per impostazione predefinita le utenze in uso.',
        permesso: 'utente.leggi',
        capitolo: 'utenti',
      },
    ],
  },
  {
    numero: '1.11.1',
    data: '20/10/2026',
    inBreve: 'quando assegni un incarico, il gestionale dice quali schede apre.',
    cambiamenti: [
      {
        testo: 'Quando assegni un incarico, l’avviso dice quali schede apre, secondo dove vale.',
        permesso: 'utente.gestisci',
        capitolo: 'utenti',
      },
    ],
  },
  {
    numero: '1.11.0',
    data: '19/10/2026',
    inBreve: 'pagina «Da completare», interventi in bozza, stampa con i loghi e PDF.',
    cambiamenti: [
      {
        testo: 'Pagina «Da completare» e percorso guidato «Completa la scheda», un dato alla volta.',
        permesso: 'persona.cerca',
        capitolo: 'nuova-scheda',
      },
      { testo: 'La Direzione registra interventi e apre percorsi.', permesso: 'intervento.crea', capitolo: 'percorsi-interventi' },
      {
        testo: '«Salva come bozza» e la pagina «Interventi» con «Completa» per finire le bozze.',
        permesso: 'intervento.leggi',
        capitolo: 'percorsi-interventi',
      },
      {
        testo: 'Stampa della scheda con i loghi del progetto, e PDF da conservare con «Scarica PDF».',
        permesso: 'persona.identificativi',
        capitolo: 'scheda',
      },
      {
        testo: 'Ricontatti dalla scheda, con l’ora, e «Com’è andato?» per chiuderli.',
        permesso: 'follow_up.gestisci',
        capitolo: 'percorsi-interventi',
      },
      {
        testo: 'Ricerca per codice fiscale in «Beneficiari».',
        permesso: 'persona.identificativi',
        capitolo: 'beneficiari',
      },
      { testo: 'Utenti: «Disattiva», «Riattiva» e «Importa da foglio».', permesso: 'utente.gestisci', capitolo: 'utenti' },
      { testo: 'Albo dei mediatori: «Unisci i doppioni».', permesso: 'mediatore.gestisci', capitolo: 'albo-mediatori' },
      {
        testo: 'Pagina «Le mie esportazioni», da cui si scaricano i file chiesti.',
        permesso: 'export.aggregato',
        capitolo: 'report-esportazioni',
      },
    ],
  },
  {
    numero: '1.10.0',
    data: '19/10/2026',
    inBreve: 'primo accesso con invito e codice a voce, mediazioni programmate dalla scheda.',
    cambiamenti: [
      {
        testo: 'Primo accesso con l’invito: un collegamento per email e un codice di sei cifre dettato a voce.',
        capitolo: 'accesso',
      },
      {
        testo: 'Mediazioni programmate dalla scheda. Il mediatore completa i dati che mancano e segna l’esito; '
          + 'l’intervento nasce in bozza.',
        permesso: ['mediazione.assegna', 'mediazione.propria'],
        capitolo: 'mediazioni',
      },
      {
        testo: 'Firme di genitore, tutore, amministratore di sostegno o responsabile della struttura, con il '
          + 'documento da caricare entro 30 giorni e gli avvisi.',
        permesso: 'consenso.registra',
        capitolo: 'consensi',
      },
      {
        testo: 'Scheda: «Perché non ha il codice fiscale» a scelta, «Altri codici» per STP, ENI e CUI, conoscenza '
          + 'dell’italiano nella sezione «Lingue».',
        permesso: 'persona.cerca',
        capitolo: 'nuova-scheda',
      },
      { testo: 'Messaggi del gestionale scritti in parole semplici, senza termini tecnici.' },
    ],
  },
]

/** La versione che l'avviso annuncia: la prima dell'elenco. */
export const ULTIMA = VERSIONI[0]
